/**
 * TI Wishlist bridge
 *
 * Logged in:
 *   - Not in wishlist → our AJAX add, stay on page (no TI wishlist redirect)
 *   - In wishlist     → /account/wishlist/
 *
 * Logged out:
 *   - Keep TI login redirect / login modal from TI Wishlist settings
 *
 * Debug: WooCommerce → Quick Buy → Debug logging, ?wcqb_debug=1,
 * or localStorage.setItem('wcqb_debug','1'). Then filter console: [BC QB][TI]
 */

import { getConfig } from '../config.js';

const BRIDGE_VERSION = '1.0.11';

function debugEnabled() {
	try {
		if (getConfig().debugLogging) {
			return true;
		}
		if (typeof window !== 'undefined' && window.localStorage.getItem('wcqb_debug') === '1') {
			return true;
		}
		if (typeof window !== 'undefined' && /(?:^|[?&])wcqb_debug=1(?:&|$)/.test(window.location.search)) {
			return true;
		}
	} catch (e) {
		// ignore
	}
	return false;
}

function tiLog(message, data) {
	if (!debugEnabled()) {
		return;
	}
	if (typeof data === 'undefined') {
		console.info('[BC QB][TI]', message);
	} else {
		console.info('[BC QB][TI]', message, data);
	}
}

function getWishlistPageUrl() {
	const config = getConfig();
	if (config.wishlistUrl) {
		return String(config.wishlistUrl);
	}
	try {
		return new URL('/account/wishlist/', window.location.origin).href;
	} catch (e) {
		return '/account/wishlist/';
	}
}

function isLoggedIn() {
	const config = getConfig();
	if (typeof config.isLoggedIn === 'boolean') {
		return config.isLoggedIn;
	}
	return !!(document.body && document.body.classList.contains('logged-in'));
}

function navigateTo(url, reason) {
	tiLog('INTENTIONAL navigate', { reason, url });
	window.location.href = url;
}

function goToWishlistPage(reason) {
	navigateTo(getWishlistPageUrl(), reason);
}

/**
 * Show TI's login / notice modal HTML (used for guests).
 */
function showTiMessageHtml(html) {
	const $ = window.jQuery;
	if (!$ || !html) {
		return;
	}
	const $msg = $(html).eq(0);
	if (!$msg.length) {
		return;
	}
	if (!$('body > .tinv-wishlist').length) {
		$('body').append($('<div>').addClass('tinv-wishlist'));
	}
	$('body > .tinv-wishlist').append($msg);
	$msg.on('click', '.tinv-close-modal, .tinvwl_button_close, .tinv-overlay', (event) => {
		event.preventDefault();
		$msg.remove();
	});
	tiLog('showed TI msg modal (guest login / notice)');
}

function normalizeLabel(value) {
	return String(value || '')
		.replace(/\s+/g, ' ')
		.trim()
		.toLowerCase();
}

/**
 * TI often leaves hidden "Already in wishlist" copy inside the control even
 * when the visible label is still "Add to Wishlist". Prefer `.tinvwl-txt`.
 */
function getVisibleWishlistLabel(link) {
	const txt = link.querySelector('.tinvwl-txt');
	if (txt) {
		return normalizeLabel(txt.textContent);
	}
	return normalizeLabel(link.textContent);
}

/**
 * TI stores data-tinv-wl-list as an object keyed by wishlist id, not an array:
 * { "1": { ID, title, url, in: [productOrVariationIds] } }
 */
function listEntries(listData) {
	if (!listData) {
		return [];
	}
	if (Array.isArray(listData)) {
		return listData;
	}
	if (typeof listData === 'object') {
		return Object.keys(listData).map((key) => listData[key]);
	}
	return [];
}

function decideWishlistState(link) {
	const text = getVisibleWishlistLabel(link);
	const rawText = normalizeLabel(link.textContent);
	const action = String(link.getAttribute('data-tinv-wl-action') || '').toLowerCase();
	const hasInList = link.classList.contains('tinvwl-product-in-list');
	const hasMakeRemove = link.classList.contains('tinvwl-product-make-remove');
	const hasAlreadyOn = link.classList.contains('tinvwl-product-already-on-wishlist');

	let listData = [];
	try {
		listData = JSON.parse(link.getAttribute('data-tinv-wl-list') || '[]');
	} catch (e) {
		listData = [];
	}

	const productId = parseInt(link.getAttribute('data-tinv-wl-product') || '0', 10);
	const variationId = parseInt(link.getAttribute('data-tinv-wl-productvariation') || '0', 10);
	const entries = listEntries(listData);

	const listContainsCurrent = entries.some((wishlist) => {
		const inn = wishlist && wishlist.in;
		if (!Array.isArray(inn)) {
			return !!inn;
		}
		const nums = inn.map((v) => parseInt(v, 10));
		// TI may store parent id and/or variation id in `in`.
		if (variationId > 0 && nums.indexOf(variationId) !== -1) {
			return true;
		}
		return nums.indexOf(productId) !== -1;
	});

	const looksLikeAdd =
		text.indexOf('add to wishlist') !== -1 ||
		text.indexOf('add to wish list') !== -1 ||
		text === 'add';

	const looksLikeBrowse =
		text.indexOf('browse wishlist') !== -1 ||
		text.indexOf('view wishlist') !== -1 ||
		(text.indexOf('already in') !== -1 && text.indexOf('add to wishlist') === -1);

	let inWishlist = false;
	let reason = 'default-add';

	// Authoritative TI signal. Do NOT use theme class
	// `tinvwl-product-already-on-wishlist` — it's present on Add buttons too.
	if (action === 'remove' || hasMakeRemove) {
		inWishlist = true;
		reason = 'action-remove-or-make-remove';
	} else if (hasInList) {
		inWishlist = true;
		reason = listContainsCurrent ? 'in-list-class-and-list-data' : 'in-list-class';
	} else if (listContainsCurrent) {
		inWishlist = true;
		reason = 'list-data-contains-product';
	} else if (looksLikeAdd) {
		inWishlist = false;
		reason = hasAlreadyOn
			? 'label-add-ignores-stale-already-on-class'
			: 'label-says-add';
	} else if (looksLikeBrowse) {
		inWishlist = true;
		reason = 'label-browse-already';
	} else {
		inWishlist = false;
		reason = 'no-in-list-signals';
	}

	return {
		inWishlist,
		reason,
		text,
		rawText,
		action,
		hasInList,
		hasMakeRemove,
		hasAlreadyOn,
		looksLikeAdd,
		looksLikeBrowse,
		listContainsCurrent,
		productId,
		variationId,
		href: link.getAttribute('href'),
		classes: link.className,
	};
}

function killHref(link) {
	if (!link || link.tagName !== 'A') {
		return;
	}
	// Remove href entirely so the browser has nothing to follow.
	link.removeAttribute('href');
	link.setAttribute('role', 'button');
	link.setAttribute('data-bc-wl-href-killed', '1');
}

function neutralizeWishlistHrefs(root = document) {
	root.querySelectorAll('a.tinvwl_add_to_wishlist_button').forEach(killHref);
}


function mergeFormValue(o1, o2) {
	if (o2 && typeof o2 === 'object' && !Array.isArray(o2)) {
		const out = typeof o1 === 'object' && o1 ? { ...o1 } : {};
		Object.keys(o2).forEach((key) => {
			out[key] = mergeFormValue(out[key], o2[key]);
		});
		return out;
	}
	return o2;
}

/**
 * Build the same FormData TI would send — without calling TI's JS.
 */
function buildAddFormData(link) {
	const $ = window.jQuery;
	const ti = window.tinvwl_add_to_wishlist;
	const productId = link.getAttribute('data-tinv-wl-product') || '0';

	const data = {
		form: {},
		tinv_wishlist_id: '',
		tinv_wishlist_name: '',
		product_type: link.getAttribute('data-tinv-wl-producttype') || '',
		product_id: productId,
		product_variation: link.getAttribute('data-tinv-wl-productvariation') || '0',
		product_action: 'addto',
		// Tell the server to stay here — we ignore response redirects anyway.
		redirect: window.location.href,
	};

	if (ti && ti.wpml) {
		data.lang = ti.wpml;
	}
	if (ti && ti.wpml_default) {
		data.lang_default = ti.wpml_default;
	}
	if (ti && ti.stats) {
		data.stats = ti.stats;
	}

	const formEls = [];
	if ($) {
		$(
			`form.cart[method=post][data-product_id="${productId}"], form.vtajaxform[method=post][data-product_id="${productId}"], form.wcqb-variation-selector[data-product_id="${productId}"]`
		).each(function collect() {
			formEls.push(this);
		});
		if (!formEls.length) {
			$(link)
				.closest('form.cart[method=post], form.vtajaxform[method=post], form.wcqb-variation-selector')
				.each(function collectClosest() {
					formEls.push(this);
				});
		}
		if (!formEls.length) {
			$('form.cart[method=post]').each(function collectAny() {
				formEls.push(this);
			});
		}
		$(`.tinv-wraper[data-tinvwl_product_id="${productId}"]`).each(function collectWrap() {
			formEls.push(this);
		});
	}

	const hiddenFields = [];
	formEls.forEach((form) => {
		form.querySelectorAll('input:not(:disabled), select:not(:disabled), textarea:not(:disabled)').forEach((el) => {
			const nameElm = el.getAttribute('name');
			const typeElm = (el.getAttribute('type') || '').toLowerCase();
			if (!nameElm || typeElm === 'button' || typeElm === 'file' || typeElm === 'submit') {
				return;
			}

			let valueElm = el.value;
			let name = nameElm;
			let count = 10;

			if ((typeElm === 'checkbox' || typeElm === 'radio') && !el.checked) {
				return;
			}

			while (/^(.+)\[([^\[\]]*?)\]$/.test(name) && count > 0) {
				const match = name.match(/^(.+)\[([^\[\]]*?)\]$/);
				if (match && match.length === 3) {
					const nested = {};
					nested[match[2]] = valueElm;
					valueElm = nested;
				}
				name = match[1];
				count -= 1;
			}

			if (typeElm === 'checkbox' || typeElm === 'radio') {
				if (!String(valueElm || '').length && typeof valueElm !== 'object') {
					valueElm = true;
				}
			}

			data.form[name] = mergeFormValue(data.form[name], valueElm);
			if (typeElm === 'hidden') {
				hiddenFields.push(nameElm);
			}
		});
	});

	// Prefer pack-selector variation if present.
	const packSelected = document.querySelector(
		`form.wcqb-variation-selector[data-product_id="${productId}"] [data-action="select-pack"].is-selected:not([disabled])`
	);
	if (packSelected) {
		const vid = packSelected.getAttribute('data-variation-id');
		if (vid) {
			data.product_variation = vid;
			data.form.variation_id = vid;
			link.setAttribute('data-tinv-wl-productvariation', String(vid));
		}
	}

	data.form['tinvwl-hidden-fields'] = hiddenFields;

	const formData = new FormData();
	Object.keys(data).forEach((key) => {
		if (key === 'form') {
			Object.keys(data.form).forEach((k) => {
				let v = data.form[k];
				if (v && typeof v === 'object') {
					v = JSON.stringify(v);
				}
				formData.append(`form[${k}]`, v);
			});
		} else {
			formData.append(key, data[key]);
		}
	});

	return { formData, data };
}

function applyWishlistSuccess(link, body) {
	const ti = window.tinvwl_add_to_wishlist;
	const $ = window.jQuery;

	if (body && body.wishlists_data && ti && ti.hash_key) {
		try {
			const raw = JSON.stringify(body.wishlists_data);
			window.localStorage.setItem(ti.hash_key, raw);
			window.sessionStorage.setItem(ti.hash_key, raw);
			tiLog('updated TI wishlist hash in storage');
		} catch (e) {
			tiLog('could not write wishlist hash', String(e));
		}
	}

	link.classList.add('tinvwl-product-in-list');
	if (body && body.wishlists_data && body.wishlists_data.products) {
		const pid = String(link.getAttribute('data-tinv-wl-product') || '');
		const entry = body.wishlists_data.products[pid];
		if (entry) {
			link.setAttribute('data-tinv-wl-list', JSON.stringify(entry));
		}
	}

	// Refresh TI counters / marks if available (does not navigate).
	if ($ && typeof $.fn.tinvwl_get_wishlist_data === 'function') {
		try {
			$.fn.tinvwl_get_wishlist_data();
		} catch (e) {
			// ignore
		}
	}

	if ($) {
		$('body').trigger('tinvwl_wishlist_added_status', [link, true]);
	}
	killHref(link);
	tiLog('add success — stayed on page; button now in-list', decideWishlistState(link));
}

/**
 * OUR ajax add — TI's JS redirect handlers are never invoked.
 */
function runBcAjaxAdd(link) {
	const $ = window.jQuery;
	const ti = window.tinvwl_add_to_wishlist;

	const state = decideWishlistState(link);
	tiLog('runBcAjaxAdd start', state);

	if (!$ ) {
		tiLog('ABORT — jQuery missing');
		return;
	}
	if (link.classList.contains('disabled-add-wishlist')) {
		tiLog('ABORT — disabled-add-wishlist');
		window.alert(
			(ti && ti.i18n_make_a_selection_text) ||
				'Please select some product options before adding this product to your wishlist.'
		);
		return;
	}

	const apiUrl = window.location.href.split('?')[0];
	const { formData, data } = buildAddFormData(link);
	tiLog('POST add payload', { apiUrl, data });

	link.classList.add('wcqb-wishlist-adding');

	$.ajax({
		url: apiUrl,
		method: 'POST',
		contentType: false,
		processData: false,
		data: formData,
		cache: false,
	})
		.done((body) => {
			link.classList.remove('wcqb-wishlist-adding');
			tiLog('add AJAX response (raw)', {
				loggedIn: isLoggedIn(),
				status: body && body.status,
				redirect: body && body.redirect,
				force_redirect: body && body.force_redirect,
				hasMsg: !!(body && body.msg),
			});

			if (!body || typeof body !== 'object') {
				tiLog('ABORT — non-object response');
				return;
			}

			const guest = !isLoggedIn();
			const dest = body.force_redirect || body.redirect || '';

			// Guests: honour TI login redirect / login modal from TI settings.
			if (guest) {
				if (dest) {
					tiLog('guest — following TI redirect (login flow)', dest);
					navigateTo(dest, 'guest-ti-redirect');
					return;
				}
				if (body.msg) {
					tiLog('guest — showing TI msg (login / notice)');
					showTiMessageHtml(body.msg);
					return;
				}
			} else if (dest) {
				// Logged in: never follow wishlist redirects after add.
				tiLog('IGNORING TI redirect/force_redirect (logged in)', {
					redirect: body.redirect,
					force_redirect: body.force_redirect,
				});
			}

			if (body.status) {
				applyWishlistSuccess(link, body);
			} else {
				tiLog('add returned status falsy', body);
			}

			// Logged-in only: drop TI success toast/modal. Guests keep login UI above.
			if (!guest) {
				window.setTimeout(() => {
					document.querySelectorAll('body > .tinv-wishlist .tinv-modal').forEach((node) => {
						node.remove();
					});
				}, 0);
			}
		})
		.fail((jqXHR, textStatus, errorThrown) => {
			link.classList.remove('wcqb-wishlist-adding');
			tiLog('add AJAX FAILED', {
				textStatus,
				errorThrown,
				status: jqXHR && jqXHR.status,
				body: jqXHR && jqXHR.responseText && String(jqXHR.responseText).slice(0, 300),
			});
		});
}

function bindWishlistButtonClicks() {
	document.addEventListener(
		'click',
		(event) => {
			const link = event.target.closest('.tinvwl_add_to_wishlist_button');
			if (!link) {
				return;
			}

			const state = decideWishlistState(link);
			tiLog('CLICK captured ' + BRIDGE_VERSION, state);

			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();
			killHref(link);

			if (state.inWishlist) {
				tiLog('decision: ALREADY IN → navigate', state.reason);
				goToWishlistPage(state.reason);
				return;
			}

			tiLog('decision: NOT IN → our AJAX add (TI click handlers skipped)', state.reason);
			runBcAjaxAdd(link);
		},
		true
	);

	document.addEventListener(
		'keydown',
		(event) => {
			const link = event.target.closest && event.target.closest('.tinvwl_add_to_wishlist_button');
			if (!link) {
				return;
			}
			const key = event.key !== undefined ? event.key : event.keyCode;
			const activate =
				key === 'Enter' || key === 13 || key === ' ' || key === 'Spacebar' || key === 32;
			if (!activate) {
				return;
			}
			event.preventDefault();
			event.stopImmediatePropagation();
			link.dispatchEvent(
				new MouseEvent('click', { bubbles: true, cancelable: true, view: window })
			);
		},
		true
	);
}

function syncTiWishlistButton(form, variation) {
	const $ = window.jQuery;
	if (!$ || !form || !variation) {
		return;
	}

	const productId = form.getAttribute('data-product_id');
	if (!productId) {
		return;
	}

	const payload = {
		variation_id: variation.variation_id,
		price_html: variation.price_html || '',
		is_in_stock: !!variation.in_stock,
		is_purchasable: !!variation.purchasable,
		variation_is_active: true,
		variation_is_visible: true,
		attributes: variation.attributes || {},
	};

	$(form).trigger('found_variation', [payload]);
	$(form).trigger('show_variation', [payload, true]);

	$(`.tinvwl_add_to_wishlist_button[data-tinv-wl-product="${productId}"]`)
		.attr('data-tinv-wl-productvariation', String(variation.variation_id))
		.removeClass('disabled-add-wishlist');

	neutralizeWishlistHrefs();
}

function selectedVariationFromForm(form) {
	const selected = form.querySelector('[data-action="select-pack"].is-selected:not([disabled])');
	const variationId =
		(selected && selected.getAttribute('data-variation-id')) ||
		(form.querySelector('input.variation_id') && form.querySelector('input.variation_id').value);
	if (!variationId || Number(variationId) <= 0) {
		return null;
	}

	let variations = [];
	try {
		variations = JSON.parse(form.getAttribute('data-product_variations') || '[]');
	} catch (e) {
		variations = [];
	}
	return (
		variations.find((v) => Number(v.variation_id) === Number(variationId)) || null
	);
}

function bindHideVariationResync() {
	const $ = window.jQuery;
	if (!$) {
		return;
	}

	$(document).on('hide_variation', 'form.wcqb-variation-selector', function onHide() {
		const form = this;
		const variation = selectedVariationFromForm(form);
		if (!variation) {
			return;
		}
		window.setTimeout(() => {
			syncTiWishlistButton(form, variation);
		}, 0);
	});
}

/**
 * Disable TI's body-level add handler so it can never race us.
 */
function disarmTiClickHandler() {
	const $ = window.jQuery;
	if (!$) {
		return;
	}
	try {
		$('body').off('click', '.tinvwl_add_to_wishlist_button');
		$('body').off('keydown', '.tinvwl_add_to_wishlist_button');
		tiLog('disarmed TI body click/keydown handlers');
	} catch (e) {
		tiLog('could not disarm TI handlers', String(e));
	}

	// Re-disarm after TI rebinds on ready/ajax.
	window.setTimeout(disarmTiClickHandlerOnce, 500);
	window.setTimeout(disarmTiClickHandlerOnce, 1500);
}

function disarmTiClickHandlerOnce() {
	const $ = window.jQuery;
	if (!$) {
		return;
	}
	try {
		$('body').off('click', '.tinvwl_add_to_wishlist_button');
		$('body').off('keydown', '.tinvwl_add_to_wishlist_button');
	} catch (e) {
		// ignore
	}
}

export function initTiWishlistBridge() {
	const buttons = document.querySelectorAll('.tinvwl_add_to_wishlist_button');
	const states = Array.from(buttons).map((b) => decideWishlistState(b));

	tiLog('init bridge ' + BRIDGE_VERSION, {
		wishlistUrl: getWishlistPageUrl(),
		buttonCount: buttons.length,
		tiPresent: typeof window.tinvwl_add_to_wishlist !== 'undefined',
		jquery: typeof window.jQuery !== 'undefined',
		loggedIn: isLoggedIn(),
		page: window.location.href,
	});
	// Plain-text so you can read state even if the object is collapsed.
	states.forEach((s, i) => {
		tiLog(
			`button[${i}] inWishlist=${s.inWishlist} reason=${s.reason} text="${s.text}" hasInList=${s.hasInList} href=${s.href}`
		);
	});

	bindWishlistButtonClicks();
	disarmTiClickHandler();
	neutralizeWishlistHrefs();
	bindHideVariationResync();

	if (window.tinvwl_add_to_wishlist) {
		window.tinvwl_add_to_wishlist.redirect_to_wishlist = '';
	}

	if (typeof MutationObserver === 'function' && document.body) {
		let scheduled = false;
		const observer = new MutationObserver(() => {
			if (scheduled) {
				return;
			}
			scheduled = true;
			window.requestAnimationFrame(() => {
				scheduled = false;
				neutralizeWishlistHrefs();
			});
		});
		observer.observe(document.body, {
			subtree: true,
			attributes: true,
			attributeFilter: ['href', 'class', 'data-tinv-wl-action', 'data-tinv-wl-list', 'data-tinv-wl-productvariation'],
		});
	}

	document.addEventListener('wcVariationSelector:variationSelected', (event) => {
		const detail = event.detail || {};
		const form =
			event.target && event.target.closest
				? event.target.closest('form.variations_form')
				: null;
		if (!form || !detail.variationId) {
			return;
		}
		const variation = selectedVariationFromForm(form);
		if (variation) {
			syncTiWishlistButton(form, variation);
		}
	});
}

export function syncTiWishlistFromPackForms(root = document) {
	const forms = root.querySelectorAll('form.wcqb-variation-selector');
	forms.forEach((form) => {
		const variation = selectedVariationFromForm(form);
		if (variation) {
			syncTiWishlistButton(form, variation);
		}
	});

	window.setTimeout(() => {
		forms.forEach((form) => {
			const variation = selectedVariationFromForm(form);
			if (variation) {
				syncTiWishlistButton(form, variation);
			}
		});
		neutralizeWishlistHrefs();
	}, 300);
}

export function debugTiWishlistReport() {
	const buttons = Array.from(document.querySelectorAll('.tinvwl_add_to_wishlist_button'));
	const report = {
		bridgeVersion: BRIDGE_VERSION,
		wishlistUrl: getWishlistPageUrl(),
		tiSettings: window.tinvwl_add_to_wishlist || null,
		buttons: buttons.map((b) => decideWishlistState(b)),
	};
	console.info('[BC QB][TI] report', report);
	return report;
}

export { BRIDGE_VERSION };
